/**
 * Semantic-segmentation mask layer.
 *
 * An HTML <canvas> sibling to the DICOM renderer. Sits above the
 * image (alpha-blended), below the SVG annotation overlay. Size
 * matches the image's intrinsic pixel dimensions, then CSS is used
 * to transform it under the viewport's zoom/pan so it co-registers
 * with the DICOM canvas exactly.
 *
 * Brush painting happens here on pointer events when the "mask"
 * tool is active. Everything else is pure render.
 */
import { createEffect, onCleanup, onMount } from "solid-js";
import type { ViewportState } from "./store";
import { classes, activeId } from "../labeling/classes";
import {
  ensureMask, getMask, brushDisc, composeMaskImage, maskVersion,
} from "../labeling/masks";

export interface MaskCanvasProps {
  viewport:    ViewportState;
  instanceUid: string;
  imageW:      number;
  imageH:      number;
  activeTool:  string;    // matches AnnotationLayer's Tool type
  brushRadius: number;
  opacity?:    number;
}

export function MaskCanvas(props: MaskCanvasProps) {
  let cvs!: HTMLCanvasElement;
  const v = props.viewport;

  let painting = false;
  let lastImgX = 0, lastImgY = 0;

  // Compute image-space coords from a pointer event by measuring
  // against the *host* (not the canvas) — the canvas itself is
  // CSS-transformed, so its bounding rect is already in transformed
  // space, which double-applies the zoom when we feed it to
  // screenToImage.  The host div covers the raw viewport and is
  // untransformed, matching what screenToImage expects.
  function imgFromEvent(e: PointerEvent): { x: number; y: number } {
    const host = cvs.parentElement!;
    const r = host.getBoundingClientRect();
    return v.screenToImage(e.clientX - r.left, e.clientY - r.top);
  }

  function insideImage(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < props.imageW && y < props.imageH;
  }

  function paint(img: { x: number; y: number }, erase: boolean) {
    // Reject strokes whose centre is outside the image. brushDisc already
    // clamps to mask bounds so we never overflow the Uint8Array, but we
    // still want mouse activity that happens over CSS-overflow areas
    // (toolbar, class bar) to be a no-op instead of painting fringe
    // pixels at the image edge.
    if (!insideImage(img.x, img.y)) {
      lastImgX = img.x;
      lastImgY = img.y;
      return;
    }
    ensureMask(props.instanceUid, props.imageW, props.imageH);
    brushDisc(props.instanceUid, img.x, img.y, props.brushRadius,
              erase ? null : activeId(), classes());
    lastImgX = img.x;
    lastImgY = img.y;
  }

  // Paint a thick "segment" between two image-space points so that a
  // fast drag doesn't leave gaps between discs. We step at most one
  // brush-radius at a time.
  function paintSegment(ax: number, ay: number, bx: number, by: number, erase: boolean) {
    const r = Math.max(1, props.brushRadius);
    const dx = bx - ax, dy = by - ay;
    const dist = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.ceil(dist / r));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      paint({ x: ax + dx * t, y: ay + dy * t }, erase);
    }
  }

  onMount(() => {
    const down = (e: PointerEvent) => {
      if (props.activeTool !== "mask") return;
      if (e.button !== 0 && e.button !== 2) return;
      painting = true;
      try { cvs.setPointerCapture(e.pointerId); } catch { /* fine */ }
      const img = imgFromEvent(e);
      paint(img, e.button === 2);
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!painting) return;
      const erase = (e.buttons & 2) !== 0;
      const img = imgFromEvent(e);
      paintSegment(lastImgX, lastImgY, img.x, img.y, erase);
    };
    const up = (e: PointerEvent) => {
      painting = false;
      try { cvs.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    };

    cvs.addEventListener("pointerdown", down);
    cvs.addEventListener("pointermove", move);
    cvs.addEventListener("pointerup", up);
    cvs.addEventListener("pointercancel", up);
    onCleanup(() => {
      cvs.removeEventListener("pointerdown", down);
      cvs.removeEventListener("pointermove", move);
      cvs.removeEventListener("pointerup", up);
      cvs.removeEventListener("pointercancel", up);
    });
  });

  // Redraw whenever the mask or the class list changes
  createEffect(() => {
    maskVersion(); classes();
    const m = getMask(props.instanceUid);
    const ctx = cvs.getContext("2d");
    if (!ctx) return;
    cvs.width = props.imageW;
    cvs.height = props.imageH;
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    if (!m) return;
    const img = composeMaskImage(m, classes(), props.opacity ?? 0.45);
    ctx.putImageData(img, 0, 0);
  });

  // Visual transform mirrors the SVG annotation layer
  const transform = () => {
    const s = v.scale();
    const o = v.imageOrigin();
    const flipSx = v.flipH() ? -1 : 1;
    const flipSy = v.flipV() ? -1 : 1;
    // The canvas is positioned at image origin and scaled by `s`.
    // Flip via CSS matrix component.
    return `translate(${o.x}px, ${o.y}px) scale(${s * flipSx}, ${s * flipSy}) ` +
           (v.flipH() ? `translate(-${props.imageW}px, 0)` : "") +
           (v.flipV() ? `translate(0, -${props.imageH}px)` : "");
  };

  return (
    <canvas
      ref={cvs}
      class="absolute left-0 top-0 origin-top-left"
      // Right-click is the erase gesture — suppress the browser context
      // menu so the user sees a clean stroke instead of a dropdown.
      onContextMenu={(e) => e.preventDefault()}
      style={{
        transform: transform(),
        "transform-origin": "top left",
        "image-rendering": "pixelated",
        "mix-blend-mode": "normal",
        // Only capture pointer events when the mask tool is active;
        // otherwise let the annotation layer / canvas handle them.
        "pointer-events": props.activeTool === "mask" ? "auto" : "none",
        "touch-action": "none",
        cursor: props.activeTool === "mask" ? "crosshair" : "default",
      }}
    />
  );
}
